import { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import SvgMapper from "../../features/common/SvgMapper";
import {
  resetStudentForm,
  setCreateStudentModal,
  updateStudentFormField,
} from "../../../store/studentManagementReducer";
import useStudentFormValidation from "../../hooks/useStudentFormValidation";
import { STUDENT_FORM_FIELDS, STUDENT_FORM_SINGLE_FIELDS } from "../../constants/StudentManagementConstants";
import { editStudent } from "../../../application/services/studentManagementService";
import "../../features/common/Style.css";
import "./Style.css";

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const FormField = ({ name, label, type = "text", placeholder, required, value, error, onChange, min, max, step, rows, disabled }) => {
  const InputComponent = rows ? "textarea" : "input";
  const baseClassName = rows 
    ? "student-modal-textarea" 
    : `student-modal-input ${error ? "student-modal-input-error" : ""} ${disabled ? "student-modal-input-disabled" : ""}`;
  
  const inputProps = {
    name,
    type: rows ? undefined : type,
    placeholder,
    value: value || "",
    onChange,
    className: baseClassName,
    disabled,
    ...(min !== undefined && { min }),
    ...(max !== undefined && { max }),
    ...(step !== undefined && { step }),
    ...(rows && { rows }),
  };

  return (
    <div className="student-modal-form-field">
      <label className="student-modal-label">
        {label} {required && <span className="student-modal-label-required">*</span>}
      </label>
      <InputComponent {...inputProps} />
      {error && <p className="student-modal-error-message">{error}</p>}
    </div>
  );
};

const CreateStudentModal = ({ onAddStudent, onUpdateStudent }) => {
  const dispatch = useDispatch();
  const formState = useSelector((state) => state.studentManagement.studentForm);
  const editingStudent = useSelector((state) => state.studentManagement.editingStudent);
  const openStudentModal = useSelector((state) => state.studentManagement.openStudentModal);
  const isEditMode = !!editingStudent;
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState("");
  const [initialFormState, setInitialFormState] = useState(null);
  const {
    formErrors,
    validateForm,
    clearFieldError,
    resetErrors,
  } = useStudentFormValidation();

  useEffect(() => {
    if (isEditMode && editingStudent && openStudentModal) {
      setInitialFormState({
        name: formState.name || "",
        email: formState.email || "",
        phone_number: formState.phone_number || "",
        address: formState.address || "",
        university: formState.university || "",
        course: formState.course || "",
        graduation_year: formState.graduation_year || "",
        gpa: formState.gpa || "",
        emergency_contact: formState.emergency_contact || "",
        notes: formState.notes || "",
      });
    } else if (!openStudentModal) {
      setInitialFormState(null);
    }
  }, [editingStudent, isEditMode, openStudentModal]);

  const hasFormChanged = () => {
    if (!isEditMode || !initialFormState) return false;
    
    return (
      (formState.name || "") !== (initialFormState.name || "") ||
      (formState.phone_number || "") !== (initialFormState.phone_number || "") ||
      (formState.address || "") !== (initialFormState.address || "") ||
      (formState.university || "") !== (initialFormState.university || "") ||
      (formState.course || "") !== (initialFormState.course || "") ||
      (formState.graduation_year || "") !== (initialFormState.graduation_year || "") ||
      (formState.gpa || "") !== (initialFormState.gpa || "") ||
      (formState.emergency_contact || "") !== (initialFormState.emergency_contact || "") ||
      (formState.notes || "") !== (initialFormState.notes || "")
    );
  };

  const hasRequiredFields = () => {
    if (isEditMode) return true;
    
    const trimmedName = formState.name?.trim() || "";
    const trimmedEmail = formState.email?.trim() || "";
    
    return trimmedName.length > 2 && trimmedEmail.length > 0 && EMAIL_REGEX.test(trimmedEmail);
  };

  const isButtonDisabled = () => {
    if (isSubmitting) return true;
    if (isEditMode) return !hasFormChanged();
    return !hasRequiredFields();
  };

  useEffect(() => {
    if (openStudentModal) {
      const originalOverflow = document.body.style.overflow;
      document.body.style.overflow = 'hidden';
      return () => {
        document.body.style.overflow = originalOverflow;
      };
    }
  }, [openStudentModal]);

  const onclose = () => {
    dispatch(resetStudentForm());
    dispatch(setCreateStudentModal(false));
    resetErrors();
    setSubmitError("");
  };

  const handleChange = (event) => {
    const { name, value } = event.target;
    let formattedValue = value;
    if (name === "graduation_year") {
      formattedValue = value === "" ? "" : Number(value);
    }
    if (name === "gpa") {
      formattedValue = value === "" ? "" : value;
    }
    if (name === "phone_number" || name === "emergency_contact") {
    formattedValue = value.replace(/\D/g, "").slice(0, 10);
   }

    dispatch(updateStudentFormField({ name, value: formattedValue }));
    if (formErrors[name]) {
      clearFieldError(name);
    }
    if (submitError) {
      setSubmitError("");
    }
  };

  const handleSubmit = async () => {
    setSubmitError("");
    setIsSubmitting(true);

    if (!validateForm(formState)) {
      setIsSubmitting(false);
      return;
    }

    try {
      if (isEditMode) {
        const payload = {
          university: formState.university?.trim() || null,
          course: formState.course?.trim() || null,
          graduation_year:
            formState.graduation_year === "" ? null : formState.graduation_year,
          gpa:
            formState.gpa === "" || formState.gpa === null
              ? null
              : Number(formState.gpa),
          phone_number: formState.phone_number?.trim() || "",
          address: formState.address?.trim() || "",
          emergency_contact: formState.emergency_contact?.trim() || "",
          notes: formState.notes?.trim() || "",
          name: formState.name?.trim(),
        };
        await editStudent(editingStudent.id, payload);
        if (onUpdateStudent) {
          onUpdateStudent();
        }
      } else {
        await onAddStudent({
          email: formState.email?.trim(),
          university: formState.university?.trim() || null,
          course: formState.course?.trim() || null,
          graduation_year:
            formState.graduation_year === "" ? null : formState.graduation_year,
          gpa:
            formState.gpa === "" || formState.gpa === null
              ? null
              : Number(formState.gpa),
          phone_number: formState.phone_number?.trim() || "",
          address: formState.address?.trim() || "",
          emergency_contact: formState.emergency_contact?.trim() || "",
          notes: formState.notes?.trim() || "",
          name: formState.name?.trim(),
        });
      }
      dispatch(resetStudentForm());
      resetErrors();
      onclose();
    } catch (error) {
      setSubmitError(error?.message || (isEditMode ? "Failed to update student. Please try again." : "Failed to add student. Please try again."));
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div 
      className="student-modal-backdrop"
    >
      <div 
        className="student-modal-container"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="student-modal-header">
          <div className="student-modal-header-content">
            <h2 className="student-modal-title">{isEditMode ? "Edit Student" : "Add New Student"}</h2>
            <button
              className="student-modal-close-button"
              onClick={() => onclose()}
            >
              {SvgMapper.closeIcon("w-6 h-6")}
            </button>
          </div>
        </div>
        <div className="student-modal-body custom-scrollbar pr-2">
          {STUDENT_FORM_FIELDS.map((row, rowIndex) => (
            <div key={rowIndex} className="student-modal-form-grid">
              {row.map((field) => (
                <FormField
                  key={field.name}
                  {...field}
                  value={formState[field.name]}
                  error={formErrors[field.name]}
                  onChange={handleChange}
                  disabled={isEditMode && field.name === "email"}
                />
              ))}
            </div>
          ))}
          {STUDENT_FORM_SINGLE_FIELDS.map((field) => (
            <FormField
              key={field.name}
              {...field}
              value={formState[field.name]}
              error={formErrors[field.name]}
              onChange={handleChange}
            />
          ))}
          {submitError && (
            <div className="student-modal-submit-error">
              {submitError}
            </div>
          )}
        </div>
        <div className="student-modal-footer">
          <button
            className="student-modal-button-cancel"
            onClick={() => onclose()}
            disabled={isSubmitting}
          >
            Cancel
          </button>
          <button
            className="student-modal-button-submit"
            onClick={handleSubmit}
            disabled={isButtonDisabled()}
          >
            {SvgMapper.createStudentIcon("w-4 h-4")}
            <span>{isSubmitting ? (isEditMode ? "Updating..." : "Adding...") : (isEditMode ? "Update Student" : "Add Student")}</span>
          </button>
        </div>
      </div>
    </div>
  );
};

export default CreateStudentModal;
